/**
 * B9 -- computed policy lifecycle, renewal links and the vehicle duplicate
 * check. Shapes mirror the shared contract in
 * docs/superpowers/specs/2026-09-15-b9-plan.md section 7 verbatim.
 *
 * Lifecycle is DERIVED on the server (`apps/policies/lifecycle.py`) from the
 * stored status and the cover dates. It is never written back; a draft or a
 * policy still pending payment has no lifecycle at all (`null`).
 */

import { formatDate } from '@/utils';

export type LifecycleStatus = 'upcoming' | 'active' | 'expired' | 'cancelled';

/** `"renewal" if is_renewal else "new"` -- read-only on the policy payload. */
export type PolicyBusinessType = 'renewal' | 'new';

/** A policy on the other end of a renewal link (`renewed_by`, detail only). */
export interface PolicyRenewalLinkRef {
  id: string;
  policy_number: string;
  display_policy_number: string;
  status: string;
}

export type RenewalRefusalCode =
  | 'renewal_source_not_found'
  | 'renewal_source_other_broker'
  | 'renewal_source_not_issued'
  | 'renewal_source_already_renewed'
  | 'renewal_source_installment'
  | 'renewal_customer_mismatch';

/** The one refusal that is a question rather than a no: the server asks the
 *  broker to confirm, and a resubmit with the acknowledgement goes through. */
export const RENEWAL_CUSTOMER_MISMATCH: RenewalRefusalCode = 'renewal_customer_mismatch';

/** A parsed renewal-link refusal off a 400. */
export interface RenewalRefusal {
  code: RenewalRefusalCode;
  message: string;
}

/** `GET /policies/renewal-prefill/?source=` -- the `eligibility` block. */
export interface RenewalEligibility {
  eligible: boolean;
  code: RenewalRefusalCode | null;
  message: string | null;
  successor: { id: string; policy_number: string } | null;
}

/** One live (Active or Upcoming) policy on the same vehicle. */
export interface VehicleCheckMatch {
  id: string;
  policy_number: string;
  insurer_policy_number: string | null;
  display_policy_number: string;
  lifecycle_status: 'active' | 'upcoming';
  inception_date: string;
  expiry_date: string;
  /** Server-formatted, e.g. "Jul 2027". */
  expiry_label: string;
  customer: string | null;
  customer_name: string;
  insurer_name: string | null;
  has_successor: boolean;
  can_renew: boolean;
  renew_refusal: RenewalRefusalCode | null;
}

/** `GET /policies/vehicle-check/?vehicle_number=&exclude_policy=`. */
export interface VehicleCheckResult {
  vehicle_number: string;
  /** False when the value is not a plausible registration number (e.g. the
   *  placeholder "NEW") -- nothing was looked up, which is NOT "no matches". */
  checked: boolean;
  reason: string | null;
  matches: VehicleCheckMatch[];
  suggested_renewal_policy_id: string | null;
}

/** The Policies list's Status filter. Values are `?lifecycle_status=`. */
export const LIFECYCLE_FILTER_OPTIONS: { value: '' | LifecycleStatus; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
];

/**
 * The words on a lifecycle badge.
 *
 * Grace does not keep a policy Active (decision D-L1): past expiry it is
 * Expired, and a policy still inside its grace period says so rather than
 * reading as plain Expired. No lifecycle (draft / pending) is an em dash, not
 * "Inactive" -- there is nothing in force or lapsed to describe.
 */
export function lifecycleBadgeLabel(policy: {
  lifecycle_status?: LifecycleStatus | null;
  lifecycle_status_display?: string | null;
  in_grace?: boolean | null;
  grace_period?: string | null;
}): string {
  const base = policy.lifecycle_status_display || '';
  if (!policy.lifecycle_status || !base) return '—';
  if (policy.in_grace) {
    return policy.grace_period
      ? `${base} · in grace until ${formatDate(policy.grace_period)}`
      : `${base} · in grace`;
  }
  return base;
}

/**
 * Normalise a vehicle number the way `apps.core.normalizers` does for the
 * lookup key: upper case, letters and digits only.
 */
export function normaliseVehicleNumber(raw: string | null | undefined): string {
  return (raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * Worth asking the server about? Mirrors the server's own plausibility rule
 * (6+ characters with a digit), so the placeholder "NEW" -- on 15 live
 * policies -- never raises a prompt.
 */
export function isCheckableVehicleNumber(normalised: string): boolean {
  return normalised.length >= 6 && /\d/.test(normalised);
}
